<template>
  <div class="cart-sidebar relative z-[100]">
    <Overlay :visible="isOpen" @click="closeSidebar" />
    <Drawer
      ref="reviewsSidebarRef"
      class="reviews-sidebar"
      :is-open="isOpen"
      data-test-id="reviews-sidebar"
      @click:close="closeSidebar"
    >
      <template #header>
        <div class="reviews-sidebar__top">
          <div class="reviews-sidebar__header">
            <div v-if="currentStep === 'detail'" class="back" @click="onBack">
              <IconBack />
            </div>
            <div class="label">{{ t('review.title') }}</div>
          </div>
        </div>
      </template>

      <div class="flex flex-col justify-between flex-1 [&>*]:px-4 pb-8">
        <Transition name="fade" mode="out-in">
          <Component
            :is="getCurrentStepComponent()"
            :key="currentStep"
            v-bind="
              currentStep === 'list'
                ? {
                    reviews,
                    averageRating,
                    reviewCount,
                    onClick: showDetailReview,
                  }
                : { review: detailReview }
            "
          />
        </Transition>
      </div>
    </Drawer>
  </div>
</template>

<script lang="ts" setup>
import { resolveComponent } from 'vue'
import type { Review } from '~/composables/types/event'
import IconBack from '~/public/icons/back.svg'

interface Props {
  reviews: Review[]
  isOpen: boolean
  averageRating: string
  reviewCount: number
  selectedReviewId?: string
}

const { t } = useI18n()

const props = withDefaults(defineProps<Props>(), {
  reviews: () => [],
  isOpen: false,
  averageRating: '',
  reviewCount: 0,
  selectedReviewId: undefined,
})

const currentStep = ref<'list' | 'detail'>('list')
const detailReview = ref<Review>({})

const getCurrentStepComponent = () => {
  if (props.selectedReviewId) {
    showDetailReview(props.selectedReviewId)
    return resolveComponent('ReviewSidebarDetail')
  }

  return currentStep.value === 'detail'
    ? resolveComponent('ReviewSidebarDetail')
    : resolveComponent('ReviewSidebarList')
}

const onBack = () => {
  currentStep.value = 'list'
  detailReview.value = {} as Review
  emit('update:selectedReviewId', undefined)
}

const emit = defineEmits(['close', 'update:selectedReviewId'])

watch(
  () => props.isOpen,
  () => {
    if (props.isOpen) {
      document.body.classList.add('prevent-scroll')
    } else {
      document.body.classList.remove('prevent-scroll')
    }
  }
)

const closeSidebar = () => {
  setTimeout(() => {
    currentStep.value = 'list'
    detailReview.value = {} as Review
  }, 500)
  emit('close')
}

const showDetailReview = (id: string) => {
  const review = props.reviews.find((r) => r.id === id)
  if (!review) return

  currentStep.value = 'detail'
  detailReview.value = review
}
</script>

<style lang="postcss" scoped>
.reviews-sidebar {
  @apply w-full md:max-w-[552px] p-0 overflow-y-scroll;

  &__top {
    @apply relative flex gap-2 items-center px-6 py-4 w-full top-0 border-b border-gray-200;
  }

  &__header {
    @apply w-full flex justify-center;

    .label {
      @apply text-lg font-semibold text-center;
    }

    .back {
      @apply absolute left-2 top-0 bottom-0 my-auto cursor-pointer w-[22px] h-[22px];

      svg {
        @apply w-full h-full;
      }
    }
  }
}
</style>

<style>
body.prevent-scroll {
  overflow: hidden;
}
</style>
